/**
 * Per-browser preference: which URL family the player picks for stream
 * playback. See docs/superpowers/specs/2026-05-10-stream-mode-toggle-and-buffer-design.md
 * for the URL-selection matrix.
 *
 *   "direct"  (default) — try the panel URL; transparently fall back to
 *                         the proxy when mixed-content (HTTPS page +
 *                         HTTP upstream) would otherwise block playback.
 *   "proxied" — always route through /api/playlists/{pid}/play/...
 *
 * Stored in localStorage under `brainzr.streamMode`. Subscribers re-render
 * via useSyncExternalStore. Cross-tab flips propagate via the standard
 * `storage` event; same-tab flips broadcast a custom `brainzr:streammode`
 * event so listeners pick up the change in the same render cycle.
 */
import { useSyncExternalStore } from "react";

export type StreamMode = "direct" | "proxied";

const STORAGE_KEY = "brainzr.streamMode";
const EVENT_NAME = "brainzr:streammode";

export function getStreamMode(): StreamMode {
  if (typeof window === "undefined") return "direct";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "proxied" ? "proxied" : "direct";
}

export function setStreamMode(m: StreamMode): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, m);
  window.dispatchEvent(new Event(EVENT_NAME));
}

function subscribe(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onLocal = () => cb();
  const onStorage = (ev: StorageEvent) => {
    if (ev.key === STORAGE_KEY) cb();
  };
  window.addEventListener(EVENT_NAME, onLocal);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(EVENT_NAME, onLocal);
    window.removeEventListener("storage", onStorage);
  };
}

export function useStreamMode(): StreamMode {
  return useSyncExternalStore(subscribe, getStreamMode, () => "direct");
}

/**
 * Which URL family a stream should actually use.
 *
 * Pure so it can be tested: the only environmental input is whether the page
 * is HTTPS, which is passed in rather than read from `window`.
 *
 * The mixed-content fallback is the part that matters. In "direct" mode the
 * panel URL is preferred — it costs this server no bandwidth and has one less
 * hop — but a plain-HTTP panel URL on an HTTPS page is blocked outright by the
 * browser, and the user just sees a player that never starts. So direct mode
 * silently falls back to the proxy in exactly that case, and only that case.
 */
export function chooseStreamUrl(
  mode: StreamMode,
  urls: { directUrl: string; proxyUrl: string | null },
  pageIsHttps: boolean,
): string {
  const { directUrl, proxyUrl } = urls;
  if (mode === "proxied" && proxyUrl) return proxyUrl;
  if (pageIsHttps && directUrl.startsWith("http://") && proxyUrl) return proxyUrl;
  return directUrl;
}
